import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Database, Cloud, Download, Trash2, RefreshCw, Loader2, CheckCircle, AlertTriangle,
  Save, Play, Eye, EyeOff, HardDrive, CloudUpload, Mail, Activity,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('de-CH', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
};

const AdminBackups = () => {
  const { token } = useAuth();
  const [local, setLocal] = useState([]);
  const [localRetention, setLocalRetention] = useState(14);
  const [isProduction, setIsProduction] = useState(true);
  const [appEnv, setAppEnv] = useState('production');
  const [cloud, setCloud] = useState({ items: [], configured: false });
  const [cfg, setCfg] = useState({
    cloud_backup_enabled: false,
    cloud_backup_endpoint: '',
    cloud_backup_access_key: '',
    cloud_backup_secret_key: '',
    cloud_backup_bucket: '',
    cloud_backup_prefix: 'zo',
    cloud_backup_region: 'us-east-1',
    cloud_backup_retention_days: 30,
  });
  const [hasAccessKey, setHasAccessKey] = useState(false);
  const [hasSecretKey, setHasSecretKey] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [health, setHealth] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        fetch(`${API_URL}/api/admin/backups`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/admin/backups/cloud/config`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/admin/backups/cloud/list`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/admin/backups/health`, { headers: authHeaders() }),
      ]);
      if (r1.ok) {
        const d = await r1.json();
        setLocal(d.local || []);
        setLocalRetention(d.local_retention_days || 14);
        setIsProduction(d.is_production !== false);
        setAppEnv(d.app_env || 'production');
      }
      if (r2.ok) {
        const d = await r2.json();
        setCfg({
          cloud_backup_enabled: !!d.cloud_backup_enabled,
          cloud_backup_endpoint: d.cloud_backup_endpoint || '',
          cloud_backup_access_key: d.cloud_backup_access_key || '',
          cloud_backup_secret_key: d.cloud_backup_secret_key || '',
          cloud_backup_bucket: d.cloud_backup_bucket || '',
          cloud_backup_prefix: d.cloud_backup_prefix || 'zo',
          cloud_backup_region: d.cloud_backup_region || 'us-east-1',
          cloud_backup_retention_days: d.cloud_backup_retention_days ?? 30,
        });
        setHasAccessKey(!!d.has_access_key);
        setHasSecretKey(!!d.has_secret_key);
      }
      if (r3.ok) {
        setCloud(await r3.json());
      }
      if (r4.ok) {
        setHealth(await r4.json());
      }
    } catch (e) {
      console.error('Fetch backups error', e);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async () => {
    setCreating(true);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/backups`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (res.ok) {
        setResult({ type: 'success', message: `Backup erstellt: ${data.filename} (${formatBytes(data.size_bytes)})${data.cloud?.ok ? ' · Cloud-Upload OK' : data.cloud ? ` · Cloud-Fehler: ${data.cloud.message}` : ''}` });
        await fetchAll();
      } else {
        setResult({ type: 'error', message: data.detail || 'Backup fehlgeschlagen' });
      }
    } catch (e) {
      setResult({ type: 'error', message: `Fehler: ${e.message}` });
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteLocal = async (filename) => {
    if (!window.confirm(`Backup "${filename}" wirklich löschen?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/backups/${encodeURIComponent(filename)}`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) {
        await fetchAll();
        setResult({ type: 'success', message: 'Lokales Backup gelöscht.' });
      }
    } catch (e) {
      setResult({ type: 'error', message: e.message });
    }
  };

  const handleDownloadLocal = (filename) => {
    const url = `${API_URL}/api/admin/backups/${encodeURIComponent(filename)}/download`;
    // Use fetch+blob to include auth header
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(e => setResult({ type: 'error', message: `Download fehlgeschlagen: ${e.message}` }));
  };

  const handleSaveConfig = async () => {
    setSavingCfg(true);
    setResult(null);
    try {
      const payload = { ...cfg };
      // Don't send masked secrets back unchanged
      if (cfg.cloud_backup_access_key.includes('•')) delete payload.cloud_backup_access_key;
      if (cfg.cloud_backup_secret_key.includes('•')) delete payload.cloud_backup_secret_key;
      const res = await fetch(`${API_URL}/api/admin/backups/cloud/config`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload),
      });
      if (res.ok) {
        setResult({ type: 'success', message: 'Cloud-Konfiguration gespeichert.' });
        await fetchAll();
      } else {
        const d = await res.json();
        setResult({ type: 'error', message: d.detail || 'Speichern fehlgeschlagen' });
      }
    } catch (e) {
      setResult({ type: 'error', message: e.message });
    } finally {
      setSavingCfg(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/backups/cloud/test`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      setResult({ type: data.ok ? 'success' : 'error', message: data.message + (data.sample_objects?.length ? ` · ${data.sample_objects.length} Objekt(e) im Prefix` : '') });
    } catch (e) {
      setResult({ type: 'error', message: e.message });
    } finally {
      setTesting(false);
    }
  };

  const handleCloudDownload = async (key) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/backups/cloud/download?key=${encodeURIComponent(key)}`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok && data.url) {
        window.open(data.url, '_blank');
      } else {
        setResult({ type: 'error', message: data.detail || 'Download-URL fehlgeschlagen' });
      }
    } catch (e) {
      setResult({ type: 'error', message: e.message });
    }
  };

  const handleCloudDelete = async (key) => {
    if (!window.confirm(`Cloud-Backup "${key}" wirklich löschen?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/backups/cloud?key=${encodeURIComponent(key)}`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) {
        await fetchAll();
        setResult({ type: 'success', message: 'Cloud-Backup gelöscht.' });
      } else {
        const d = await res.json();
        setResult({ type: 'error', message: d.detail || 'Löschen fehlgeschlagen' });
      }
    } catch (e) {
      setResult({ type: 'error', message: e.message });
    }
  };

  const handleSendHealthReport = async () => {
    setSendingReport(true);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/backups/health-report`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (res.ok) {
        setResult({ type: 'success', message: data.message });
      } else {
        setResult({ type: 'error', message: data.detail || 'Versand fehlgeschlagen' });
      }
    } catch (e) {
      setResult({ type: 'error', message: e.message });
    } finally {
      setSendingReport(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--primary))]" /></div>;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="max-w-5xl space-y-6" data-testid="admin-backups-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Database className="w-6 h-6 text-[hsl(var(--primary))]" /> Backups
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Tägliches mongodump → tar.gz · 14 Tage lokale Rotation · automatischer Upload zu Infomaniak Swiss Backup
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={fetchAll} data-testid="backups-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Aktualisieren
          </Button>
          <Button onClick={handleCreate} disabled={creating} data-testid="backups-create-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
            Backup jetzt erstellen
          </Button>
        </div>
      </div>

      {result && (
        <Alert className={result.type === 'success' ? 'bg-[hsl(var(--chart-2)/0.1)] border-[hsl(var(--chart-2))]' : 'bg-[hsl(var(--destructive)/0.1)] border-[hsl(var(--destructive))]'} data-testid="backup-result-alert">
          <AlertDescription className={`text-sm ${result.type === 'success' ? 'text-[hsl(var(--chart-2))]' : 'text-[hsl(var(--destructive))]'}`}>
            {result.type === 'success' ? <CheckCircle className="w-4 h-4 inline mr-2" /> : <AlertTriangle className="w-4 h-4 inline mr-2" />}
            {result.message}
          </AlertDescription>
        </Alert>
      )}

      {!isProduction && (
        <Alert className="bg-amber-50 border-amber-300" data-testid="preview-env-banner">
          <AlertDescription className="text-sm text-amber-900">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            <strong>Preview-Umgebung erkannt (APP_ENV={appEnv}).</strong> Cloud-Uploads zu Infomaniak sind <strong>deaktiviert</strong>, damit Testdaten nicht in den Produktions-Bucket gelangen. Lokale Backups werden weiterhin erstellt. Auf Produktion setze in <code className="bg-amber-100 px-1 rounded text-xs">backend/.env</code> die Variable <code className="bg-amber-100 px-1 rounded text-xs">APP_ENV=production</code>.
          </AlertDescription>
        </Alert>
      )}

      {/* Health Status */}
      {health && (
        <Card className={`card-shadow ${health.healthy ? 'border-l-4 border-l-[hsl(var(--chart-2))]' : 'border-l-4 border-l-[hsl(var(--destructive))]'}`} data-testid="health-status-card">
          <CardHeader>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className={`w-5 h-5 ${health.healthy ? 'text-[hsl(var(--chart-2))]' : 'text-[hsl(var(--destructive))]'}`} />
                  Backup-Status: {health.healthy ? 'OK · Alles in Ordnung' : 'Achtung · Bitte prüfen'}
                </CardTitle>
                <CardDescription>
                  {health.last_backup_at
                    ? `Letztes Backup vor ${health.days_since_last_backup?.toFixed(1) ?? '?'} Tagen (${formatDate(health.last_backup_at)})`
                    : 'Noch kein Backup vorhanden.'}
                </CardDescription>
              </div>
              <Button variant="secondary" onClick={handleSendHealthReport} disabled={sendingReport} data-testid="send-health-report-btn">
                {sendingReport ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Health-Report jetzt senden
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-lg border border-[hsl(var(--border))] p-4 bg-[hsl(var(--muted)/0.2)]">
                <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1 flex items-center gap-2">
                  <HardDrive className="w-3.5 h-3.5" /> Lokal
                </div>
                <div className="text-2xl font-semibold">{health.local.count}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{health.local.total_human} · {health.local.retention_days} Tage Retention</div>
              </div>
              <div className="rounded-lg border border-[hsl(var(--border))] p-4 bg-[hsl(var(--muted)/0.2)]">
                <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1 flex items-center gap-2">
                  <Cloud className="w-3.5 h-3.5" /> Cloud (Infomaniak)
                </div>
                <div className="text-2xl font-semibold">{health.cloud.enabled ? health.cloud.count : '—'}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                  {health.cloud.enabled
                    ? `${health.cloud.total_human} · ${health.cloud.retention_days} Tage Retention`
                    : 'Cloud-Upload deaktiviert'}
                </div>
              </div>
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3">
              Der Health-Report wird automatisch am 1. jedes Monats um 09:00 versendet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Cloud Config */}
      <Card className="card-shadow" data-testid="cloud-config-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Cloud className="w-5 h-5 text-[hsl(var(--primary))]" /> Cloud-Backup Konfiguration</CardTitle>
          <CardDescription>S3-kompatible Cloud (Infomaniak Swiss Backup). Backups werden automatisch nach dem Erstellen hochgeladen.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <input type="checkbox" id="cb_enabled" checked={cfg.cloud_backup_enabled}
              onChange={(e) => setCfg({ ...cfg, cloud_backup_enabled: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300" data-testid="cb-enabled" />
            <Label htmlFor="cb_enabled" className="cursor-pointer">Cloud-Upload aktivieren</Label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Endpoint URL</Label>
              <Input value={cfg.cloud_backup_endpoint} onChange={(e) => setCfg({ ...cfg, cloud_backup_endpoint: e.target.value })}
                placeholder="https://s3.swiss-backup04.infomaniak.com" data-testid="cb-endpoint" />
            </div>
            <div>
              <Label>Bucket</Label>
              <Input value={cfg.cloud_backup_bucket} onChange={(e) => setCfg({ ...cfg, cloud_backup_bucket: e.target.value })}
                placeholder="emergent-apps-backup" data-testid="cb-bucket" />
            </div>
            <div>
              <Label>Prefix (App-ID)</Label>
              <Input value={cfg.cloud_backup_prefix} onChange={(e) => setCfg({ ...cfg, cloud_backup_prefix: e.target.value })}
                placeholder="zo" data-testid="cb-prefix" />
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Eindeutig pro App, trennt Backups im geteilten Bucket.</p>
            </div>
            <div>
              <Label>Region</Label>
              <Input value={cfg.cloud_backup_region} onChange={(e) => setCfg({ ...cfg, cloud_backup_region: e.target.value })}
                placeholder="us-east-1" data-testid="cb-region" />
            </div>
            <div>
              <Label>Access Key ID</Label>
              <div className="relative">
                <Input type={showAccess ? 'text' : 'password'} value={cfg.cloud_backup_access_key}
                  onChange={(e) => setCfg({ ...cfg, cloud_backup_access_key: e.target.value })}
                  placeholder={hasAccessKey ? '••••••••' : 'Access Key'}
                  className="pr-10 font-mono text-sm" data-testid="cb-access-key" />
                <button type="button" onClick={() => setShowAccess(!showAccess)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]">
                  {showAccess ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Secret Key</Label>
              <div className="relative">
                <Input type={showSecret ? 'text' : 'password'} value={cfg.cloud_backup_secret_key}
                  onChange={(e) => setCfg({ ...cfg, cloud_backup_secret_key: e.target.value })}
                  placeholder={hasSecretKey ? '••••••••' : 'Secret Key'}
                  className="pr-10 font-mono text-sm" data-testid="cb-secret-key" />
                <button type="button" onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]">
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Cloud-Retention (Tage)</Label>
              <Input type="number" min="1" max="3650" value={cfg.cloud_backup_retention_days}
                onChange={(e) => setCfg({ ...cfg, cloud_backup_retention_days: parseInt(e.target.value) || 30 })}
                data-testid="cb-retention" />
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Ältere Cloud-Backups werden automatisch gelöscht.</p>
            </div>
          </div>

          <div className="flex gap-2 pt-2 border-t border-[hsl(var(--border))]">
            <Button onClick={handleSaveConfig} disabled={savingCfg} data-testid="cb-save-btn">
              {savingCfg ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Speichern
            </Button>
            <Button variant="secondary" onClick={handleTest} disabled={testing} data-testid="cb-test-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CloudUpload className="w-4 h-4 mr-2" />}
              Verbindung testen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Local Backups */}
      <Card className="card-shadow" data-testid="local-backups-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><HardDrive className="w-5 h-5" /> Lokale Backups ({local.length})</CardTitle>
          <CardDescription>Speicherort: /app/backend/backups · Rotation: {localRetention} Tage</CardDescription>
        </CardHeader>
        <CardContent>
          {local.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))] py-4 text-center">Noch keine Backups vorhanden.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-[hsl(var(--border))]">
                  <tr className="text-left text-[hsl(var(--muted-foreground))]">
                    <th className="py-2 font-medium">Datei</th>
                    <th className="py-2 font-medium">Grösse</th>
                    <th className="py-2 font-medium">Erstellt</th>
                    <th className="py-2 font-medium text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {local.map((b) => (
                    <tr key={b.filename} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted)/0.3)]">
                      <td className="py-2 font-mono text-xs">{b.filename}</td>
                      <td className="py-2">{formatBytes(b.size_bytes)}</td>
                      <td className="py-2 text-[hsl(var(--muted-foreground))]">{formatDate(b.created_at)}</td>
                      <td className="py-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => handleDownloadLocal(b.filename)} title="Herunterladen" data-testid={`download-local-${b.filename}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteLocal(b.filename)} title="Löschen" className="text-[hsl(var(--destructive))]" data-testid={`delete-local-${b.filename}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cloud Backups */}
      <Card className="card-shadow" data-testid="cloud-backups-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Cloud className="w-5 h-5 text-[hsl(var(--primary))]" /> Cloud-Backups ({cloud.items?.length || 0})</CardTitle>
          <CardDescription>
            {cloud.configured ? `Bucket: ${cloud.bucket} · Prefix: ${cloud.prefix}/` : 'Cloud noch nicht konfiguriert.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!cloud.configured ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))] py-4 text-center">Bitte zuerst Cloud-Konfiguration speichern und Verbindung testen.</p>
          ) : (cloud.items || []).length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))] py-4 text-center">Keine Cloud-Backups gefunden.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-[hsl(var(--border))]">
                  <tr className="text-left text-[hsl(var(--muted-foreground))]">
                    <th className="py-2 font-medium">Datei</th>
                    <th className="py-2 font-medium">Grösse</th>
                    <th className="py-2 font-medium">Hochgeladen</th>
                    <th className="py-2 font-medium text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {cloud.items.map((b) => (
                    <tr key={b.key} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted)/0.3)]">
                      <td className="py-2 font-mono text-xs">{b.filename}</td>
                      <td className="py-2">{formatBytes(b.size_bytes)}</td>
                      <td className="py-2 text-[hsl(var(--muted-foreground))]">{formatDate(b.last_modified)}</td>
                      <td className="py-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => handleCloudDownload(b.key)} title="Download-Link" data-testid={`download-cloud-${b.filename}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleCloudDelete(b.key)} title="Löschen" className="text-[hsl(var(--destructive))]" data-testid={`delete-cloud-${b.filename}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default AdminBackups;
